import { useAppSelector } from "@app/hooks";
import { RootState } from "@app/store";
import ExpandableSnack from "@features/Common/ExpandableSnack";
import { AttentionLevel } from "@features/home-notifications/types";
import { ManifestDevice } from "@features/plan-manifest/types";
import { Category, Measure } from "@lib/labels";
import { SnackType } from "@lib/types";
import { getErrorMessage } from "@lib/utils";
import WaterDamageIcon from '@mui/icons-material/WaterDamage';
import { useSnackbar } from "notistack";
import { useEffect, useMemo, useState } from "react";
import { useHealthCheckHomeMutation } from "../api";
import { HealthCheckResult, HealthCheckType } from "../types";
import { getHealthCheckSentiment } from "../utils";
import HealthCheckSummary from "./HealthCheckSummary";

const LeakHealth = () => {
  const { enqueueSnackbar } = useSnackbar();
  const property = useAppSelector((state: RootState) => state.property);
  const userTenant = useAppSelector((state: RootState) => state.userTenant);
  const tenant = useAppSelector((state: RootState) => state.tenant);

  const currentTenant = useMemo(() => tenant.currentTenant || userTenant, [tenant.currentTenant, userTenant]);
  const manifestEntries = useMemo(() => property.manifestEntries, [property.manifestEntries]);

  const [healthCheckResult, setHealthCheckResult] = useState<HealthCheckResult | null>(null);

  const [
    healthCheckHome,
  ] = useHealthCheckHomeMutation();

  useEffect(() => {
    if (currentTenant.tenant_id && property.property_id) {
      healthCheckHome({
        healthCheckType: HealthCheckType.LEAK,
        propertyId: property.property_id,
        userTenantId: currentTenant.tenant_id,
      }).then((res) => {
        if ('error' in res) {
          enqueueSnackbar("Failed to perform health check:", {
            key: "leak-hc-error",
            content: (
              <ExpandableSnack
                id="leak-hc-error"
                message={"Couldn't perform health check:"}
                variant={SnackType.error}
                detail={getErrorMessage(res.error)}
              />),
          });
        } else {
          setHealthCheckResult(res.data);
        }
      });
    }
  }, [healthCheckHome, property.property_id, currentTenant.tenant_id, enqueueSnackbar]);

  const involvedEntries = useMemo(() => manifestEntries
    ?.filter(ee => (ee.device as ManifestDevice).sensors.some(ss => (
      ss.sensor_category === Category.water && ss.measure_name === Measure.usage_rate))), [manifestEntries]);

  const status = useMemo(() => {
    switch(getHealthCheckSentiment(healthCheckResult)) {
      default:
      case AttentionLevel.warning:
        return 'Status is unknown.';
      case AttentionLevel.urgent:
        return 'Water leak detected.';
      case AttentionLevel.info:
        return 'Waiting on healthcheck to complete.';
      case undefined:
        return 'No water leaks detected.';
    }
  }, [healthCheckResult]);

  const substatus = useMemo(() => {
    if (!healthCheckResult || !healthCheckResult.leak || !healthCheckResult.leak.length) {
      return "No moisture sensor readings";
    }

    const areas = healthCheckResult.leak
      .map((l) => l.property_area) || ['the home'];

    const leakAreas = healthCheckResult.leak
      .filter(l => l.leak_detected)
      .map((l) => l.property_area) || ['the home'];

    switch (getHealthCheckSentiment(healthCheckResult)) {
      case AttentionLevel.warning:
        return `No readings from sensors in ${areas.join(', ')} area${areas.length > 1 ? 's' : ''}`;

      case AttentionLevel.urgent:
        return `Some sensors in ${leakAreas.join(', ')} area${areas.length > 1 ? 's' : ''} detected moisture`;

      case AttentionLevel.info:
        return `Waiting on readings from sensors in ${areas.join(', ')}`;

      default:
        return `All sensors in ${areas.join(', ')} area${areas.length > 1 ? 's' : ''} are dry`;
    }
  }, [healthCheckResult]);

  return (
    <HealthCheckSummary
      icon={<WaterDamageIcon sx={{ fontSize: '6em' }} />}
      healthCheckResult={healthCheckResult}
      title="Water Leaks"
      entries={involvedEntries || []}
      status={status}
      substatus={substatus}
    />
  );
}

export default LeakHealth;
